// triggerFormHelpers — shared helpers for `TriggerEditFormBody`. Extracted to
// keep the form orchestrator under the 200-line CLAUDE.md budget.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D6 — Trigger shell UI.

import type { SqlEngine, QualifiedName } from '../../../../sql-grammar/model';
import type {
  TriggerEvent,
  TriggerGranularity,
  TriggerShellDef,
  TriggerTiming,
} from '../../../../sql-resolver/types/ddlEdit';
import type { ParsedParkedTrigger } from '../../../../sql-resolver/ddl/extract/triggerTypes';

export type TriggerEditMode = 'create' | 'alter';

export interface TriggerFormValues {
  readonly name: string;
  readonly target: string;
  readonly timing: TriggerTiming;
  readonly events: ReadonlyArray<TriggerEvent>;
  readonly bodyText: string;
  readonly orReplace: boolean;
  readonly mssqlOrAlter: boolean;
  readonly granularity: TriggerGranularity | '';
  readonly pgsqlExecuteFunction: string;
  readonly oracleEditionable: boolean;
  readonly sqliteTemporary: boolean;
  /** ENABLE/DISABLE state for alter mode. */
  readonly enabled: boolean;
}

export const DEFAULT_TRIGGER_FORM_VALUES: TriggerFormValues = {
  name: '',
  target: '',
  timing: 'after',
  events: ['insert'],
  bodyText: '',
  orReplace: false,
  mssqlOrAlter: false,
  granularity: '',
  pgsqlExecuteFunction: '',
  oracleEditionable: false,
  sqliteTemporary: false,
  enabled: true,
};

export function valuesFromParsed(parsed: ParsedParkedTrigger): Partial<TriggerFormValues> {
  return {
    name: parsed.name.name,
    target: parsed.target?.name ?? '',
    ...(parsed.timing ? { timing: parsed.timing } : {}),
    ...(parsed.events && parsed.events.length > 0 ? { events: parsed.events } : {}),
    ...(parsed.bodyText ? { bodyText: parsed.bodyText } : {}),
    ...(parsed.orReplace ? { orReplace: true } : {}),
    ...(parsed.mssqlOrAlter ? { mssqlOrAlter: true } : {}),
    ...(parsed.granularity ? { granularity: parsed.granularity } : {}),
    ...(parsed.pgsqlExecuteFunction ? { pgsqlExecuteFunction: parsed.pgsqlExecuteFunction } : {}),
    ...(parsed.oracleEditionable ? { oracleEditionable: true } : {}),
    ...(parsed.sqliteTemporary ? { sqliteTemporary: true } : {}),
  };
}

function makeQualifiedName(text: string): QualifiedName {
  const trimmed = text.trim();
  const dotIdx = trimmed.indexOf('.');
  if (dotIdx > 0) {
    const schema = trimmed.slice(0, dotIdx).trim();
    const name = trimmed.slice(dotIdx + 1).trim();
    return {
      schema, name, raw: trimmed, quoted: false,
      foldedName: name.toLowerCase(),
      foldedSchema: schema.toLowerCase(),
    };
  }
  return {
    name: trimmed, raw: trimmed, quoted: false,
    foldedName: trimmed.toLowerCase(),
  };
}

export function buildTriggerShellDef(
  engine: SqlEngine, values: TriggerFormValues,
): TriggerShellDef {
  return {
    name: makeQualifiedName(values.name),
    target: makeQualifiedName(values.target),
    timing: values.timing,
    events: values.events,
    bodyText: values.bodyText,
    ...(values.mssqlOrAlter && engine === 'mssql' ? { mssqlOrAlter: true } : {}),
    ...(values.orReplace && (engine === 'postgres' || engine === 'oracle') ? { orReplace: true } : {}),
    ...(values.granularity && (engine === 'postgres' || engine === 'oracle' || engine === 'sqlite')
      ? { granularity: values.granularity as TriggerGranularity } : {}),
    ...(values.pgsqlExecuteFunction.trim().length > 0 && engine === 'postgres'
      ? { pgsqlExecuteFunction: values.pgsqlExecuteFunction.trim() } : {}),
    ...(values.oracleEditionable && engine === 'oracle' ? { oracleEditionable: true } : {}),
    ...(values.sqliteTemporary && engine === 'sqlite' ? { sqliteTemporary: true } : {}),
  };
}

export function toggleEvent(
  events: ReadonlyArray<TriggerEvent>, event: TriggerEvent,
): ReadonlyArray<TriggerEvent> {
  if (events.includes(event)) return events.filter((e) => e !== event);
  const order: ReadonlyArray<TriggerEvent> = ['insert', 'update', 'delete'];
  return order.filter((e) => events.includes(e) || e === event);
}

export const TIMING_OPTIONS: ReadonlyArray<{ value: TriggerTiming; label: string }> = [
  { value: 'before', label: 'BEFORE' },
  { value: 'after', label: 'AFTER' },
  { value: 'instead-of', label: 'INSTEAD OF' },
];

export const EVENT_OPTIONS: ReadonlyArray<{ value: TriggerEvent; label: string }> = [
  { value: 'insert', label: 'INSERT' },
  { value: 'update', label: 'UPDATE' },
  { value: 'delete', label: 'DELETE' },
];

export const GRANULARITY_OPTIONS: ReadonlyArray<{ value: '' | TriggerGranularity; label: string }> = [
  { value: '', label: '— default —' },
  { value: 'row', label: 'FOR EACH ROW' },
  { value: 'statement', label: 'FOR EACH STATEMENT (pgsql)' },
];
